/**
 * Формирование перечня приложений к заявлению (ТЗ п. 6.4, FR-15…FR-17).
 *
 * Тексты пунктов и условия их включения лежат в справочнике `appendix-items`
 * (файл data/appendix-items.json → таблица в базе), а не в коде: состав пакета
 * меняется чаще, чем выходит новая версия приложения.
 *
 * Нумерация сквозная и непрерывная: исключение условного пункта
 * перенумеровывает последующие.
 */

import type { AppendixItemEntry } from '../types/dictionaries.js';
import type { CaseProfile } from '../types/profile.js';
import type { BranchingFlags } from './branching.js';
import { caseCreditBureaus } from './credit-bureaus.js';

export interface AppendixLine {
  /** Порядковый номер в готовом перечне, начиная с 1 */
  num: number;
  key: string;
  text: string;
  /** Пункт добавлен пользователем вручную (ТЗ п. 6.4, последняя строка таблицы) */
  custom: boolean;
  /** Отмечен ли документ как фактически собранный (FR-18) */
  collected: boolean;
  /** Отмечен «не прикладывается»: в документ не идёт, номера нет (num = 0) */
  excluded: boolean;
}

/**
 * Разбор условия включения. Поддерживаются:
 *   `null`                — безусловный пункт;
 *   `has_children`        — флаг ветвления;
 *   `!has_children`       — отрицание флага;
 *   `is_married|is_divorced` — дизъюнкция (любой из флагов);
 *   `is_married&has_children` — конъюнкция.
 * Более сложных выражений сознательно нет: условие должно оставаться
 * читаемым администратору, который правит справочник.
 */
export function evaluateCondition(condition: string | null, flags: BranchingFlags): boolean {
  if (!condition || condition.trim() === '') return true;
  const expr = condition.trim();

  const evalAtom = (atom: string): boolean => {
    const a = atom.trim();
    if (a.startsWith('!')) return !evalAtom(a.slice(1));
    const value = (flags as unknown as Record<string, boolean>)[a];
    if (value === undefined) {
      throw new Error(`Неизвестное условие включения пункта приложений: «${a}»`);
    }
    return value;
  };

  if (expr.includes('|')) return expr.split('|').some(evalAtom);
  if (expr.includes('&')) return expr.split('&').every(evalAtom);
  return evalAtom(expr);
}

export interface BuildAppendixOptions {
  items: AppendixItemEntry[];
  profile: CaseProfile;
  flags: BranchingFlags;
  /** Вернуть и пункты «не прикладывается» — для вкладки, где их отмечают */
  withExcluded?: boolean;
}

/*
  Условия, которые действуют, даже если в справочнике условие не задано
  (сверка «Айнбиндер», 26.10.2026: в перечне стояли «Постановления ФССП»,
  которых в деле не было). Справочник общий для порталов и правится в базе;
  правило — здесь, чтобы не зависеть от правки записи.
*/
const BUILT_IN_CONDITIONS: Record<string, string> = { fssp: 'has_fssp_debts' };

export const CREDIT_REPORT = 'credit_report';
/** Отчёт второго бюро — отдельная строка перечня (01.11.2026) */
export const SECOND_CREDIT_REPORT = 'credit_report_2';

export function buildAppendix({ items, profile, flags, withExcluded = false }: BuildAppendixOptions): AppendixLine[] {
  const checklist = profile.legal.appendixChecklist ?? {};
  const excluded = profile.legal.appendixExcluded ?? {};

  const base = items
    .filter((item) => !item.archived)
    .slice()
    .sort((a, b) => a.order - b.order)
    .filter((item) => evaluateCondition(item.condition ?? BUILT_IN_CONDITIONS[item.key] ?? null, flags))
    .flatMap((item) => {
      /*
        Два бюро — два отчёта (решение заказчика 01.11.2026): «Кредитный отчёт
        (АО «НБКИ»)» и «Кредитный отчёт (АО «ОКБ»)». Второй — под ключом
        credit_report_2: собран и «не прикладывается» отмечаются отдельно.
        Одно бюро — пункт как в справочнике, без скобок (ответ на вопрос 11).
      */
      const bureaus = item.key === CREDIT_REPORT ? caseCreditBureaus(profile.legal) : [];
      const variants =
        bureaus.length > 1
          ? bureaus.map((bureau, i) => ({ key: i === 0 ? item.key : SECOND_CREDIT_REPORT, text: `${item.text} (${bureau.name})` }))
          : [{ key: item.key, text: item.text }];
      return variants.map(({ key, text }) => ({
        key,
        text,
        custom: false,
        collected: checklist[key] === true,
        excluded: excluded[key] === true,
      }));
    });

  // Пользовательские пункты вставляются в указанную позицию (1-based).
  // Пункт без позиции добавляется в конец.
  const extras = profile.legal.extraAppendixItems ?? [];
  const result = base.slice();
  const tail: typeof base = [];

  for (const extra of extras) {
    const line = {
      key: `custom:${extra.id}`,
      text: extra.text,
      custom: true,
      collected: checklist[`custom:${extra.id}`] === true,
      excluded: excluded[`custom:${extra.id}`] === true,
    };
    if (extra.position === null || extra.position === undefined) tail.push(line);
    else result.splice(Math.max(0, Math.min(result.length, extra.position - 1)), 0, line);
  }
  result.push(...tail);

  // Сквозная нумерация присваивается в последнюю очередь — после всех включений
  // и вставок. Это и есть требование FR-15.
  // Пункты «не прикладывается» номера не получают и в документ не идут
  let num = 0;
  return result
    .filter((line) => withExcluded || !line.excluded)
    .map((line) => ({ num: line.excluded ? 0 : ++num, ...line }));
}

/** Позиции перечня, не отмеченные как собранные (предупреждение FR-18, раздел 6.8). */
export function missingAppendixDocuments(lines: AppendixLine[]): AppendixLine[] {
  return lines.filter((l) => !l.collected);
}
